import { Action, ActionReducer } from '@ngrx/store';
import omit from 'lodash/omit';
import { PremiumDifference } from 'sharedModels/premium-difference';
import { localStorageSync } from 'ngrx-store-localstorage';
import { Events } from 'shared/base/generics/globals/gtm-data-layer/enums/events.enum';
import { FormTypes } from 'shared/base/generics/globals/gtm-data-layer/enums/form-type.enum';
import { Names } from 'shared/base/generics/globals/gtm-data-layer/enums/names.enum';
import { DataLayer } from 'shared/base/generics/globals/gtm-data-layer/models/data-layer.model';
import { localStorageDeserializer } from 'shared/base/generics/globals/local-storage-sync/local-storage-deserializer';
import { cmsFeatureKey } from 'sharedStores/cms/cms.reducer';
import { DropOffActionTypes } from 'sharedStores/drop-off/drop-off.actions';
import { dropOffFeatureKey } from 'sharedStores/drop-off/drop-off.reducer';
import { errorFeatureKey } from 'sharedStores/error/error.reducer';
import {
    GenericHouseholdMemberActionTypes,
    GenericPersonalActionTypes,
} from 'stores/personal/personal.actions';
import { personalFeatureKey } from 'stores/personal/personal.reducer';
import { quoteControllerFeatureKey } from 'sharedStores/quote-controller/quote-controller.reducer';
import { GenericQuoteActionTypes, QuoteActionTypes } from 'stores/quote/quote.actions';
import { quoteFeatureKey } from 'stores/quote/quote.reducer';
import { ResumeQuoteActionTypes } from 'stores/resume-quote/resume-quote.actions';
import { staticDataFeatureKey } from 'stores/static-data/static-data.reducer';
import {
    GenericVehicleActionTypes,
    SetVehicleTrackerDetailsActionTypes,
    TrackingDeviceRequirementsActionTypes,
} from 'stores/vehicle/vehicle.actions';
import { vehicleFeatureKey } from 'stores/vehicle/vehicle.reducer';
import { authFeatureKey } from 'stores/auth/auth.reducer';
import { RootState } from 'stores/root-state';
import { homeContentsFeatureKey } from 'stores/home-contents/home-contents.reducer';
import { documentFeatureKey } from 'stores/document/document.reducer';
import {
    AddressHomeContentActionTypes,
    GenericHomeContentActionTypes,
    IncidentHistoryHomeContentActionTypes,
    SecurityMeasuresHomeContentActionTypes,
} from 'stores/home-contents/home-contents.actions';
import { getCallbackReason } from 'sharedHelpers/callback-reason-helper';
import { QuoteControllerActionTypes } from 'sharedStores/quote-controller/quote-controller.actions';
import { calculatePremium } from '../helpers/premium-helper';
import { GenericResumeQuoteActionTypes } from './resume-quote/resume-quote.actions';
import { resumeQuoteFeatureKey } from './resume-quote/resume-quote.reducer';
import { paymentFeatureKey } from './payment-details/payment.reducer';
import { GenericPaymentDetailsActionTypes } from './payment-details/payment.actions';

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
    /*
     * The function localStorageSync contains the following if statement:
       if (
        (config.storage === undefined && !config.checkStorageAvailability) ||
        (config.checkStorageAvailability && checkIsBrowserEnv())
        ) {
            config.storage = localStorage || window.localStorage;
        }
     *
     * This means that if we are doing an SSR build, you need to set checkStorageAvailability = true
     * otherwise the package tries to access window, which is not defined.
     * Additionally if we are running on the browser, we need to set checkStorageAvailability = false,
     * otherwise the package will default the storage property back to localStorage
     *
     * This seems like a bug in the logic of the package and this is our workaround for this
     *
     * Additionally note that if you are busy with an SSR execution then sessionStorage will not be defined
     * and as such we need to set storage: undefined if we are not running on the browser
     * */

    const isBrowserEnvironment = typeof window !== 'undefined';

    return localStorageSync({
        keys: [
            { [quoteControllerFeatureKey]: { deserialize: localStorageDeserializer } },
            { [personalFeatureKey]: { deserialize: localStorageDeserializer } },
            { [vehicleFeatureKey]: { deserialize: localStorageDeserializer } },
            { [quoteFeatureKey]: { deserialize: localStorageDeserializer } },
            { [paymentFeatureKey]: { deserialize: localStorageDeserializer } },
            { [errorFeatureKey]: { deserialize: localStorageDeserializer } },
            { [dropOffFeatureKey]: { deserialize: localStorageDeserializer } },
            { [staticDataFeatureKey]: { deserialize: localStorageDeserializer } },
            { [resumeQuoteFeatureKey]: { deserialize: localStorageDeserializer } },
            { [homeContentsFeatureKey]: { deserialize: localStorageDeserializer } },
            { [documentFeatureKey]: { deserialize: localStorageDeserializer } },
            authFeatureKey,
            cmsFeatureKey,
        ],
        rehydrate: true,
        checkStorageAvailability: !isBrowserEnvironment,
        restoreDates: true,
        storage: isBrowserEnvironment ? sessionStorage : undefined,
    })(reducer);
}

/**
 * This reducer automatically submits each user step to the datalayer with custom processing for some of the events
 * @param reducer
 */
export function dataLayerSubmitReducer(reducer: ActionReducer<any>): ActionReducer<any> {
    return function (state, action) {
        let dataLayer: DataLayer;

        const castAction = <any>action;

        switch (castAction.type) {
            case QuoteActionTypes.CREATE_LEAD_RESPONSE:
            case ResumeQuoteActionTypes.START_RESUME_QUOTE:
                dataLayer = initDataLayer(action, state);
                submitStartQuote(dataLayer, castAction, state);
                break;

            case QuoteActionTypes.SEND_RESUME_LINK_REQUEST:
            case QuoteActionTypes.SAVE_AND_SEND_QUOTE:
                dataLayer = initDataLayer(action, state);
                submitSaveQuote(castAction, dataLayer);
                break;

            case QuoteControllerActionTypes.SAVE_GCLID_AND_CID:
                dataLayer = initDataLayer(action, state);
                submitGCLID(castAction, dataLayer);
                break;

            case isActionType(GenericPersonalActionTypes, castAction.type):
                dataLayer = initDataLayer(action, state);
                submitPersonalActions(state, castAction, dataLayer);
                break;

            case isActionType(GenericHouseholdMemberActionTypes, castAction.type):
                dataLayer = initDataLayer(action, state);
                submitHouseholdMemberActions(state, castAction, dataLayer);
                break;

            case isActionType(GenericVehicleActionTypes, castAction.type):
            case isActionType(TrackingDeviceRequirementsActionTypes, castAction.type):
            case isActionType(SetVehicleTrackerDetailsActionTypes, castAction.type):
                dataLayer = initDataLayer(action, state);
                submitVehicleActions(dataLayer, state, castAction);
                break;

            case isActionType(GenericHomeContentActionTypes, castAction.type):
            case isActionType(IncidentHistoryHomeContentActionTypes, castAction.type):
            case isActionType(SecurityMeasuresHomeContentActionTypes, castAction.type):
                dataLayer = initDataLayer(action, state);
                submitHomeContentsActions(dataLayer, state, castAction);
                break;

            case isActionType(GenericQuoteActionTypes, castAction.type):
                dataLayer = initDataLayer(action, state);
                submitQuoteActions(dataLayer, state);
                break;

            case isActionType(GenericPaymentDetailsActionTypes, castAction.type):
                dataLayer = initDataLayer(action, state);
                submitPaymentActions(dataLayer);
                break;

            case GenericPaymentDetailsActionTypes.ACCOUNT_DETAILS:
                dataLayer = initDataLayer(action, state);
                submitAccountDetails(dataLayer, state);
                break;

            case QuoteActionTypes.PREMIUM_VIEWED:
                dataLayer = initDataLayer(action, state);
                submitPremiumViewedAction(state, dataLayer);
                break;

            case QuoteActionTypes.HOME_CONTENTS_PREMIUM_VIEWED:
                dataLayer = initDataLayer(action, state);
                submitHomeContentsPremiumViewedAction(state, dataLayer);
                break;

            case QuoteActionTypes.SET_RISK_ADDRESS_REQUEST:
            case isActionType(AddressHomeContentActionTypes, castAction.type):
                dataLayer = initDataLayer(action, state);
                submitRiskAddressActions(dataLayer, castAction);
                break;

            case DropOffActionTypes.SET_DROP_OFF_STATE:
                dataLayer = initDataLayer(action, state);
                submitDropoffActions(castAction, dataLayer, state);
                break;

            case DropOffActionTypes.DROP_OFF_REQUEST:
                dataLayer = initDataLayer(action, state);
                submitCallbackAction(castAction, dataLayer, state);
                break;

            case isActionType(GenericResumeQuoteActionTypes, castAction.type):
                dataLayer = initDataLayer(action, state);
                submitResumeQuoteActions(castAction, dataLayer, state);
                break;
        }
        return reducer(state, action);
    };
}

/**
 * Initialize the datalayer for the event
 *
 * @param action
 * @param state
 */
function initDataLayer(action: Action, state: RootState) {
    const abTestId =
        state?.quoteController?.ssrAbTestVariants?.squidexAbTestId ??
        state?.quoteController?.abTestVariants?.squidexAbTestId ??
        null;
    const abTestType =
        state?.quoteController?.ssrAbTestVariants?.squidexAbTestType ??
        state?.quoteController?.abTestVariants?.squidexAbTestType ??
        null;

    return new DataLayer({
        event: Events.SUCCESS_FORM_SUBMIT,
        form: {
            name: Names.INSTANT_QUOTE,
            type: FormTypes.INSTANT_QUOTE,
            stepName: action.type,
        },
        route: state?.quoteController?.route,
        user: { isLoggedIn: false },
        leadNumber: state?.quote?.leadNumber,
        googleClientId: state?.quote?.googleClientId,
        googleClickId: state?.quote?.googleClickId,
        policyNumber: state?.quote?.policyNumber,
        vdn: state?.quote?.vdn,
        utm:
            state?.quote?.queryParams &&
            omit(
                state?.quote?.queryParams,
                Object.keys(state?.quote?.queryParams).filter((key) => !key.startsWith('utm')),
            ),
        webtrends_test_id: abTestId,
        webtrends_test_type: abTestType,
        excluded_from_test: String(abTestId === '' && abTestType === ''),
    });
}

const isActionType = (actionTypes: any, type: any) => {
    if (Object.values(actionTypes).includes(type)) {
        return type;
    }
    return false;
};

function submitStartQuote(dataLayer: DataLayer, castAction: any, state: RootState) {
    const isTriggComms = getIsTriggComms(state);
    const triggCommType = castAction.type.replace('[resume quote]', '[trigg comms]');
    const stepName = isTriggComms ? triggCommType : castAction.type;
    const source = getUtmSource(state);

    dataLayer.form.stepName = `${stepName} - ${source}`;

    dataLayer.setEvent(Events.START_FORM);
    if (castAction?.vdn) {
        dataLayer.setCustomProperty('vdn', castAction?.vdn);
    }
    if (castAction?.leadNumber) {
        dataLayer.setCustomProperty('leadNumber', castAction?.leadNumber);
    }

    dataLayer.submit();
}

function submitSaveQuote(castAction: any, dataLayer: DataLayer) {
    if (castAction?.communicationType)
        dataLayer.form.stepName += ` - ${castAction?.communicationType?.toLowerCase()}`;

    dataLayer.submit();
}

function submitGCLID(castAction: any, dataLayer: DataLayer) {
    if (castAction?.googleClientId)
        dataLayer.setCustomProperty('googleClientId', castAction?.googleClientId);
    if (castAction?.googleClickId)
        dataLayer.setCustomProperty('googleClickId', castAction?.googleClickId);

    dataLayer.submit();
}

function submitPersonalActions(state: RootState, castAction: any, dataLayer: DataLayer) {
    const policyHolder: any = {
        ...state?.personal?.policyHolder,
        ...castAction.person,
    };

    dataLayer
        .setCustomProperty('policyHolder', {
            ...omit(
                policyHolder,
                ...[
                    'firstName',
                    'lastName',
                    'identification',
                    'emailAddress',
                    'phone',
                    'whatsAppNumber',
                ],
            ),
        })
        .submit();
}

function submitHouseholdMemberActions(state: RootState, castAction: any, dataLayer: DataLayer) {
    const householdMember: any = {
        ...state?.personal?.householdMembers,
        ...castAction.person,
    };

    dataLayer
        .setCustomProperty('householdMember', {
            ...omit(
                householdMember,
                ...['firstName', 'lastName', 'idNumber', 'emailAddress', 'phone'],
            ),
        })
        .submit();
}

function submitVehicleActions(dataLayer: DataLayer, state: RootState, castAction: any) {
    dataLayer
        .setCustomProperty('vehicle', {
            ...state?.vehicle?.vehicle,
            ...castAction.vehicle,
        })
        .submit();
}

function submitHomeContentsActions(dataLayer: DataLayer, state: RootState, castAction: any) {
    dataLayer
        .setCustomProperty('homeContents', {
            ...state?.homeContents?.homeContents,
            ...castAction.homeContents,
        })
        .submit();
}

function submitQuoteActions(dataLayer: DataLayer, state: RootState) {
    dataLayer
        .setCustomProperty('quote', {
            viewedPremium: state?.quote?.viewedPremium,
            premium: state?.quote?.premium,
            onlinePremium: state?.quote?.onlinePremium,
            cashbackPremium: state?.quote?.cashbackPremium,
            cashbackBonus: state?.quote?.cashbackPayout,
            hasCashback: state?.quote?.hasCashback,
        })
        .submit();
}

function submitPaymentActions(dataLayer: DataLayer) {
    dataLayer.submit();
}

function submitAccountDetails(dataLayer: DataLayer, state: RootState) {
    dataLayer
        .setCustomProperty('avsDetails', {
            attempts: state.payment.paymentDetailsHistory.length,
        })
        .submit();
}

function submitPremiumViewedAction(state: RootState, dataLayer: DataLayer) {
    const premiumDifferences = calculatePremium(
        state?.vehicle?.vehicle?.optionalExtraCover,
        state?.vehicle?.vehicle?.excessAmount?.premiumDifferences ?? new PremiumDifference(),
    );
    const premium: number = state?.quote?.onlinePremium;
    dataLayer
        .setEvent(Events.GENERATE_LEAD)
        .setCustomProperty('premium', {
            value: premium + premiumDifferences.discountDifference,
        })
        .setCustomProperty('contactID', state?.quote?.leadNumber)
        .submit();
}

function submitHomeContentsPremiumViewedAction(state: RootState, dataLayer: DataLayer) {
    const premium: number = state?.quote?.premium;
    dataLayer
        .setEvent(Events.GENERATE_LEAD)
        .setCustomProperty('premium', {
            value: premium,
        })
        .setCustomProperty('contactID', state?.quote?.leadNumber)
        .submit();
}

function submitRiskAddressActions(dataLayer: DataLayer, castAction: any) {
    dataLayer.setCustomProperty('riskAddress', castAction.riskAddress).submit();
}

function submitDropoffActions(castAction: any, dataLayer: DataLayer, state: RootState) {
    if (castAction.dropOffState) {
        dataLayer
            .setCustomProperty('dropOff', {
                ...state?.callback?.dropOffState,
                ...castAction.dropOffState,
            })
            .submit();
    }
}

function submitCallbackAction(castAction: any, dataLayer: DataLayer, state: RootState) {
    const callbackReason = getCallbackReason(state?.callback?.dropOffState?.code);
    dataLayer
        .setCustomProperty('callback', {
            callbackReason,
            ...state?.callback?.dropOffState,
        })
        .submit();
}

/**
 * For Trigger Comms we need to submit the same actions as for Resume Quote just named [trigg comms] instead of [resume quote]
 * To simplify the code, we're just doing a string replace here just before submitting to the datalayer. This is because
 * only the datalayer needs to differentiate between the two for tracking purposes. For us the functionality of the FE
 * stays exactly the same.
 * @param castAction
 * @param dataLayer
 * @param state
 */
function submitResumeQuoteActions(castAction: any, dataLayer: DataLayer, state: RootState) {
    const isTriggComms = getIsTriggComms(state);
    const triggCommType = castAction.type.replace('[resume quote]', '[trigg comms]');
    const stepName = isTriggComms ? triggCommType : castAction.type;
    const source = getUtmSource(state);
    dataLayer.form.stepName = `${stepName} - ${source}`;

    if (GenericResumeQuoteActionTypes.ID_NUMBER_INCORRECT === castAction.type) {
        if (castAction.resumeQuote.idChecks.idTriesLeft > 0) {
            dataLayer.form.stepName = `${stepName} - ${castAction.resumeQuote.idChecks.idTriesLeft} attempts left`;
        } else {
            dataLayer.form.stepName = `${stepName} - user blocked`;
        }
    }
    dataLayer.submit();
}

function getIsTriggComms(state: RootState) {
    return state?.quote?.queryParams
        ? state?.quote.queryParams['utm_source']?.includes('trigger_comm')
        : false;
}

function getUtmSource(state: RootState) {
    let source = state?.quote.queryParams['utm_source'];
    if (source) {
        source = source.substring(0, source.indexOf('_'));
    }

    return source;
}
